import React, {createContext, useContext, useEffect, useRef, useState} from 'react';
import {useFetchHook} from "../hooks/useFetchHook";
import fetcher from "../fetcher";
import useDidMountEffect from "../hooks/useDidMountEffect";
import _ from 'lodash'
import {useDebounce} from "@uidotdev/usehooks";
import {useModalsStore} from "../stores/modalsStore";
import {useDefaultQuoteSettingsContext} from "./DefaultQuoteSettingsContext";
import {useTaxRegionFlow} from "../hooks/useTaxRegionFlow";

export const CalcContext = createContext(undefined)

const initialCarState = {
    makes: [],
    models: [],
    trims: [],
    options: [],

    base_car_id: null,
    year: '',
    make: '',
    model: '',
    trim: '',
    option_codes: [],
    vin_required: null,
    vin_vehicles: [],
}

const getInitialCalcState = (quoteSettings) => {
    // One default seeds both: customer_location is what Settings calls "the"
    // default; the Dealer only moves away from it when picked on its own.
    const customerLocation = quoteSettings.customer_location
    const dealerLocation = quoteSettings.customer_location

    return {
        base_car_id: null,
        option_code_ids: [],
        msrp: '',
        msrp_plus: '',
        msrp_minus: '',
        msrp_percentage: '',

        invoice: '',
        invoice_plus: '',
        invoice_minus: '',

        cost: '',
        is_purchase: false,
        vin: '',

        term: quoteSettings.term,
        profit: quoteSettings.profit,
        mileage: quoteSettings.mileage,
        mileage_alt: quoteSettings.mileage_alt,
        drive_off: quoteSettings.drive_off,
        is_drive_off_cap_reduction: quoteSettings.is_drive_off_cap_reduction,

        // A copy, so a reset always re-syncs the Customer field, even to the
        // location it already holds (a pick it shows may never have landed).
        location: {...customerLocation},
        location_id: customerLocation.id,
        dealer_location: dealerLocation,
        dealer_location_id: dealerLocation.id,

        address_line_one: customerLocation.address_line_one,
        address_line_two: customerLocation.address_line_two,
        city: customerLocation.city,
        state: customerLocation.state,
        zipcode: customerLocation.zipcode,
        county: customerLocation.county,

        dealer_address_line_one: dealerLocation.address_line_one,
        dealer_address_line_two: dealerLocation.address_line_two,
        dealer_city: dealerLocation.city,
        dealer_state: dealerLocation.state,
        dealer_zipcode: dealerLocation.zipcode,
        dealer_county: dealerLocation.county,

        current_mileage: 0,
        is_certified_preowned: true,

        // The MarketScan tax region the user picked for this ZIP, when the ZIP
        // has more than one. Transient frontend state only — it rides along in
        // the desking and create payloads, where the backend validates it and
        // stores it on the resulting quote. null means "no explicit choice",
        // which is every ZIP with a single region.
        tax_region_id: null,

        rebates: []
    }
}

export const CalcContextProvider = ({ children, id } ) => {
    const { quoteSettings } = useDefaultQuoteSettingsContext()
    // Seed new/used from the URL (?is_used=) so a used listing decodes correctly.
    // Initializer form so it doesn't fire the reset-on-isUsed-change effect.
    const [isUsed, setIsUsed] = useState(
        () => new URLSearchParams(window.location.search).get('is_used') === 'true'
    )
    const [carState, setCarState] = useState(initialCarState)

    const [calcState, setCalcState] = useState(getInitialCalcState(quoteSettings))

    // When opened from a CRM opportunity (/calculate?opportunity_id=), load it so
    // the header can link back to the person and the created deal gets attached.
    const [opportunity, setOpportunity] = useState(null)

    const [getMakesForYearResponse, getMakesForYear] = useFetchHook(fetcher.makesForYear)
    const [getModelsForMakeResponse, getModelsForMake] = useFetchHook(fetcher.modelsForMake)
    const [getTrimsForModelResponse, getTrimsForModel] = useFetchHook(fetcher.trimsForModel)
    const [getCarByVinResponse, getCarByVin] = useFetchHook(fetcher.getCarByVin)

    const [createDealResponse, createDeal] = useFetchHook(fetcher.createDeal)

    const closeModalById = useModalsStore((state) => state.closeModalById)
    const {resolveTaxRegion, isLookingUp: isLookingUpTaxRegions} = useTaxRegionFlow()

    // When the calculator is opened pre-filled from the ListingsPage "Select"
    // button (/calculate?vin=...&msrp=...), the VIN drives the normal decode and
    // this holds the listing's combined MSRP to apply once the car fills.
    const urlSeededRef = useRef(false)
    const urlMsrpOverrideRef = useRef(null)

    // Location picks in the order they were made, and the latest one to target
    // each slot, so a pick finishing after its tax-region lookup never
    // overwrites a slot that a newer pick has already claimed.
    const locationPickCountRef = useRef(0)
    const latestLocationPickRef = useRef({customer: 0, dealer: 0})

    // A reset claims both slots as the newest pick, so a pick still waiting on
    // its lookup can no longer land on top of it.
    const invalidatePendingLocationPicks = () => {
        const pick = ++locationPickCountRef.current
        latestLocationPickRef.current = {customer: pick, dealer: pick}
    }

    useEffect(function seedFromUrlParams(){
        if (urlSeededRef.current) return
        urlSeededRef.current = true

        const params = new URLSearchParams(window.location.search)
        const vin = params.get('vin')
        const msrp = params.get('msrp')
        if (vin) {
            if (msrp) urlMsrpOverrideRef.current = Number(msrp)
            // Set post-mount so the getCarByVin decode effect fires.
            setCalcState(prev => ({...prev, vin: vin.toUpperCase()}))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const resetCarState = () => {
        setCarState(initialCarState)
    }

    const resetCalcState = () => {
        invalidatePendingLocationPicks()
        setCarState(initialCarState)
        setCalcState(getInitialCalcState(quoteSettings))
    }

    // Apply a listing chosen in the ListingsPageModal to the calculator. Same as
    // the /calculate?vin=&msrp= flow: the VIN drives the decode (fills the
    // vehicle) and the listing's combined MSRP is applied once the car fills.
    // With no VIN, just set the MSRP directly.
    const applyListing = ({vin, msrp} = {}) => {
        const numericMsrp = (msrp != null && msrp !== '') ? Number(msrp) : null
        if (vin) {
            if (numericMsrp != null) urlMsrpOverrideRef.current = numericMsrp
            setCalcState(prev => ({...prev, vin: vin.toUpperCase()}))
        } else if (numericMsrp != null) {
            setCalcState(prev => ({...prev, msrp: numericMsrp}))
        }
    }

    const getLeadId = () => {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get('lead_id')) {
            return urlParams.get('lead_id')
        }
    }

    const getOpportunityId = () => new URLSearchParams(window.location.search).get('opportunity_id')

    useEffect(function loadOpportunityFromUrl(){
        const opportunityId = getOpportunityId()
        if (!opportunityId) return
        fetcher.getCrmOpportunity(opportunityId)
            .then((res) => {
                setOpportunity(res?.data || null)
                // Start at the customer's saved location; the dealer stays put.
                const {zipcode, city, state, county} = res?.data?.person_detail || {}
                if (zipcode) saveLocation({zipcode, city, state, county}, true, false)
            })
            .catch(() => {})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // `newDealerLocation`: the modal can stage a different Dealer pick in the same call.
    const saveLocation = async (location, shouldSetCustomerLocation, shouldSetDealerLocation, newDealerLocation = location) => {
        // Every Calculate location path — the Locations modal and the CRM
        // seed — funnels through here, so the tax-region check lives at this
        // one point.
        //
        // Only the CUSTOMER ZIP determines tax, so a dealer-only change never
        // prompts. Awaiting the modal before touching calcState means the
        // selection is already in state by the time the user can hit Desk, and
        // no quote is ever scanned at the auto-picked rate first.
        const pick = ++locationPickCountRef.current
        if (shouldSetCustomerLocation) latestLocationPickRef.current.customer = pick
        if (shouldSetDealerLocation) latestLocationPickRef.current.dealer = pick

        let taxRegionId = null
        if (shouldSetCustomerLocation) {
            taxRegionId = await resolveTaxRegion(location, {
                isPurchase: calcState?.is_purchase,
                // No prompt for a pick that something newer has replaced.
                isStale: () => latestLocationPickRef.current.customer !== pick,
            })
        }

        // A newer pick of the same slot wins, whichever finishes first.
        const isLatestCustomerPick = latestLocationPickRef.current.customer === pick
        const isLatestDealerPick = latestLocationPickRef.current.dealer === pick

        let updateObj = {}
        if (shouldSetCustomerLocation && isLatestCustomerPick) {
            updateObj = {
                // A new ZIP invalidates any previous choice; resolveTaxRegion
                // returns null unless the user just made a new one.
                tax_region_id: taxRegionId,
                location: location,
                location_id: location.id,
                address_line_one: location.address_line_one,
                address_line_two: location.address_line_two,
                city: location.city,
                state: location.state,
                zipcode: location.zipcode,
                county: location.county,
            }
        }
        if (shouldSetDealerLocation && isLatestDealerPick) {
            updateObj = {
                ...updateObj,
                dealer_location: newDealerLocation,
                dealer_location_id: newDealerLocation.id,
                dealer_address_line_one: newDealerLocation.address_line_one,
                dealer_address_line_two: newDealerLocation.address_line_two,
                dealer_city: newDealerLocation.city,
                dealer_state: newDealerLocation.state,
                dealer_zipcode: newDealerLocation.zipcode,
                dealer_county: newDealerLocation.county
            }
        }
        if (!_.isEmpty(updateObj)) {
            // Functional: the tax-region await above can outlive other edits.
            setCalcState(prev => ({...prev, ...updateObj}))
        }

        closeModalById("LocationModal")
    }

    useDidMountEffect(function resetStateOnIsUsedChange(){
        resetCalcState()
    }, [isUsed])

    useDidMountEffect(function getCarByVinFromBE(){
        const stripped = calcState.vin.trim()
        if (stripped.length >= 17) {
            getCarByVin(stripped, isUsed)
        }
    }, [calcState.vin])

    useDidMountEffect(function setMakes() {
        if (!getMakesForYearResponse?.loading && getMakesForYearResponse?.data?.data) {
            let makes = getMakesForYearResponse?.data?.data

            makes = _.sortBy(makes, ['name'])
            setCarState({
                ...carState,
                makes: makes,
                vin_required: null,
            })
        }
    }, [getMakesForYearResponse])

    useDidMountEffect(function setModels(){
        if (!getModelsForMakeResponse?.loading && getModelsForMakeResponse?.data?.data) {
            const models = getModelsForMakeResponse?.data?.data
            setCarState({
                ...carState,
                models: models,
                vin_required: null,
            })
        }
    }, [getModelsForMakeResponse])

    useDidMountEffect(function setTrims(){
        if (!getTrimsForModelResponse?.loading && getTrimsForModelResponse?.data?.data) {
            const trims = getTrimsForModelResponse?.data?.data
            setCarState({
                ...carState,
                trims: trims,
                vin_required: null,
            })
        }
    }, [getTrimsForModelResponse])


    useDidMountEffect(function setCarByVinFromBE(){
        if (!getCarByVinResponse?.loading && getCarByVinResponse?.data?.data) {
            const data = getCarByVinResponse?.data?.data
            let trim = data.trim || ''
            const last_deal_data = trim?.last_deal_data
            const base_car_id = trim?.id
            const options = trim.option_codes || []

            let option_codes = []
            if (options.length === 1) {
                option_codes = options
            }

            if (trim){
                trim = JSON.stringify(trim)
            }

            setCarState({
                ...carState,
                year: data.year,
                make: data.make.cleaned_name,
                model: data.model.cleaned_name,
                trim: trim,
                options: options,
                vin_vehicles: data?.vin_vehicles,

                base_car_id: base_car_id,

                makes: data.makes,
                models: data.models,
                trims: data.trims,
                option_codes: option_codes,
            })

            // If opened from a listing (?msrp=combined_msrp), use that MSRP
            // instead of the decoded catalog MSRP. One-shot: cleared after use.
            const overrideMsrp = urlMsrpOverrideRef.current
            urlMsrpOverrideRef.current = null

            setCalcState({
                ...calcState,
                car_id: base_car_id,
                base_car_id: base_car_id,
                msrp: overrideMsrp ?? last_deal_data?.msrp,
                cost: last_deal_data?.cost,
                // profit: last_deal_data?.profit,
            })
        }
    }, [getCarByVinResponse])

    useDidMountEffect(function updateCalcStateWhenQuoteSettingsChange(){
        if (quoteSettings) {
            invalidatePendingLocationPicks()
            const customerLocation = quoteSettings.customer_location
            const dealerLocation = quoteSettings.customer_location
            setCalcState({
                ...calcState,
                profit: quoteSettings.profit,
                drive_off: quoteSettings.drive_off,
                is_drive_off_cap_reduction: quoteSettings.is_drive_off_cap_reduction,
                mileage: quoteSettings.mileage,
                mileage_alt: quoteSettings.mileage_alt,

                location: {...customerLocation},
                location_id: quoteSettings.customer_location.id,
                address_line_one: customerLocation.address_line_one,
                address_line_two: customerLocation.address_line_two,
                city: customerLocation.city,
                state: customerLocation.state,
                zipcode: customerLocation.zipcode,
                county: customerLocation.county,

                // A region belongs to the ZIP it was picked for; keep it only
                // when the customer ZIP isn't moving.
                tax_region_id: customerLocation.zipcode === calcState.zipcode ? calcState.tax_region_id : null,

                dealer_location: dealerLocation,
                dealer_location_id: dealerLocation.id,
                dealer_address_line_one: dealerLocation.address_line_one,
                dealer_address_line_two: dealerLocation.address_line_two,
                dealer_city: dealerLocation.city,
                dealer_state: dealerLocation.state,
                dealer_zipcode: dealerLocation.zipcode,
                dealer_county: dealerLocation.county
            })
        }
    }, [quoteSettings])

    const value = {
        carState,
        setCarState,

        calcState,
        setCalcState,

        getMakesForYear,
        getMakesForYearResponse,

        getModelsForMake,
        getModelsForMakeResponse,

        getTrimsForModel,
        getTrimsForModelResponse,

        getCarByVin,
        getCarByVinResponse,

        resetCarState,
        resetCalcState,
        applyListing,

        createDeal,
        createDealResponse,

        getLeadId,
        getOpportunityId,
        opportunity,
        isUsed,
        setIsUsed,

        saveLocation,
        isLookingUpTaxRegions,
    }

    if (!carState) return null;

    return (
        <CalcContext.Provider value={value}>
            { children }
        </CalcContext.Provider>
    )
}

export const useCalcContext = () => {
    const context = useContext(CalcContext)

    if (context === undefined) {
        throw new Error('useCalcContext must be used within CalcContext')
    }
    return context
}